import type { ColumnFilterItem, ColumnsType } from "antd/es/table/interface";
import {
  DateRenderer,
  ResolutionTag,
} from "@/views/DataDiscrepancies/Components";
import {
  createFilters,
  getCellWidth,
  renderCell,
  getFixedColumns,
  getFormattedTitles,
  getHiddenColumns,
  getSearchableColumns,
  getSorter,
  tableFilter,
} from "@/utils/tableConfigs/GlobalFunctions";
import type { TableRow } from "./issuesTableConfig";

type ColumnBuilderConfig = {
  rows: TableRow[];
  columnOrder: readonly string[];
  titleMapping: Record<string, string>;
  columnWidths: Record<string, number>;
  searchableColumns: Set<string>;
  fixedColumns: Set<string>;
  hiddenColumns?: Set<string>;
  renderMap?: Record<string, (text: unknown) => React.ReactNode>;
  customRender?: (
    header: string,
    text: unknown,
    record: TableRow,
  ) => React.ReactNode | undefined;
};

export const renderDateCell = (text: unknown) =>
  typeof text === "string" && text ? <DateRenderer dateString={text} /> : "-";

export const renderResolutionTagCell = (text: unknown) => (
  <ResolutionTag status={text} />
);

const getOrderedHeaders = (
  rows: TableRow[],
  columnOrder: readonly string[],
) => {
  const availableHeaders = Object.keys(rows[0] || {});

  return [
    ...columnOrder.filter((header) => availableHeaders.includes(header)),
    ...availableHeaders.filter((header) => !columnOrder.includes(header)),
  ];
};

export const buildColumns = ({
  rows,
  columnOrder,
  titleMapping,
  columnWidths,
  searchableColumns,
  fixedColumns,
  hiddenColumns,
  renderMap = {},
  customRender,
}: ColumnBuilderConfig): ColumnsType<TableRow> => {
  if (!rows.length) return [];

  return getOrderedHeaders(rows, columnOrder).map((header) => ({
    title: () => getFormattedTitles(header, titleMapping),
    dataIndex: header,
    key: header,
    sorter: getSorter(header),
    render: (text: unknown, record: TableRow) =>
      customRender?.(header, text, record) ??
      renderMap[header]?.(text) ??
      renderCell(text, header),
    filters: createFilters(rows, header) as ColumnFilterItem[],
    onFilter: (value, record) => tableFilter(value, record, header),
    fixed: (getFixedColumns(header, fixedColumns) || undefined) as
      | "left"
      | undefined,
    width: getCellWidth(header, columnWidths),
    filterSearch: getSearchableColumns(header, searchableColumns),
    hidden: hiddenColumns ? getHiddenColumns(header, hiddenColumns) : undefined,
  }));
};
